import {
  ChangeDetectionStrategy,
  Component,
  Input,
  OnInit,
} from '@angular/core';
import { DatastoreCollection } from '@freelancer/datastore';
import {
  Publication,
  PublicationsCollection,
} from '@freelancer/datastore/collections';
import { ButtonColor } from '@freelancer/ui/button';
import { HeadingType } from '@freelancer/ui/heading';
import { trackById } from '@freelancer/ui/helpers';
import { ListItemPadding, ListItemType } from '@freelancer/ui/list-item';
import { Margin } from '@freelancer/ui/margin';
import {
  FontColor,
  FontStyle,
  FontType,
  FontWeight,
  TextSize,
} from '@freelancer/ui/text';
import * as Rx from 'rxjs';
import { UserProfileViewState } from '../../user-profile-editable-ui/user-profile-editable-ui.model';

@Component({
  selector: 'app-user-profile-publications',
  template: `
    <ng-container *ngIf="!(publicationsCollection.status$ | flAsync)?.error">
      <ng-container *ngIf="publications$ | flAsync as publications">
        <fl-card
          *ngIf="
            (publications && publications.length > 0) ||
            (isViewerProfileOwner$ | flAsync)
          "
        >
          <fl-card-header-title i18n="Publications card title">
            Publications
          </fl-card-header-title>

          <fl-card-header-right
            *ngIf="
              (isViewerProfileOwner$ | flAsync) &&
              (currentViewState$ | flAsync) === UserProfileViewState.VIEW
            "
          >
            <fl-button
              i18n="Add publication button"
              flTrackingLabel="UserProfileAddPublication"
              [color]="ButtonColor.SECONDARY"
              (click)="handleAddPublication()"
            >
              Add Publication
            </fl-button>
          </fl-card-header-right>

          <ng-container
            *ngIf="(currentViewState$ | flAsync) === UserProfileViewState.VIEW"
          >
            <ng-container
              *ngIf="publications && publications.length > 0; else emptyState"
            >
              <fl-list [outerPadding]="false" [padding]="ListItemPadding.SMALL">
                <fl-list-item
                  *ngFor="let publication of publications; trackBy: trackById"
                >
                  <app-user-profile-publications-view
                    [editable]="isViewerProfileOwner$ | flAsync"
                    [publication]="publication"
                    [publicationsCollection]="publicationsCollection"
                    (edit)="handlePublicationEditAction($event)"
                  ></app-user-profile-publications-view>
                </fl-list-item>
              </fl-list>
            </ng-container>
            <ng-template #emptyState>
              <fl-text
                i18n="Empty publications message"
                [size]="TextSize.SMALL"
                [style]="FontStyle.ITALIC"
              >
                No publications have been added.
              </fl-text>
            </ng-template>
          </ng-container>

          <ng-container
            *ngIf="(currentViewState$ | flAsync) === UserProfileViewState.EDIT"
          >
            <app-user-profile-publications-edit
              [publication$]="currentEditingPublication$"
              [publicationsCollection]="publicationsCollection"
              (cancel)="handlePublicationEditCancel()"
            ></app-user-profile-publications-edit>
          </ng-container>
        </fl-card>
      </ng-container>
    </ng-container>
  `,
  styleUrls: ['./user-profile-publications.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class UserProfilePublicationsComponent implements OnInit {
  ButtonColor = ButtonColor;
  UserProfileViewState = UserProfileViewState;
  FontColor = FontColor;
  TextSize = TextSize;
  FontStyle = FontStyle;
  FontType = FontType;
  FontWeight = FontWeight;
  HeadingType = HeadingType;
  ListItemPadding = ListItemPadding;
  ListItemType = ListItemType;
  Margin = Margin;
  trackById = trackById;

  @Input() publicationsCollection: DatastoreCollection<PublicationsCollection>;
  @Input() isViewerProfileOwner$: Rx.Observable<boolean>;

  private _currentViewState$ = new Rx.BehaviorSubject<UserProfileViewState>(
    UserProfileViewState.VIEW,
  );
  currentViewState$ = this._currentViewState$.asObservable();
  publications$: Rx.Observable<readonly Publication[]>;

  private _currentEditingPublication$ = new Rx.BehaviorSubject<
    Publication | undefined
  >(undefined);
  currentEditingPublication$ = this._currentEditingPublication$.asObservable();

  ngOnInit() {
    this.publications$ = this.publicationsCollection.valueChanges();
  }

  handleAddPublication(): void {
    this.clearCurrentEditingPublication();
    this.switchToPublicationEditState();
  }

  handlePublicationEditAction(publication: Publication): void {
    this._currentEditingPublication$.next(publication);
    this.switchToPublicationEditState();
  }

  handlePublicationEditCancel(): void {
    this.clearCurrentEditingPublication();
    this.switchToPublicationViewState();
  }

  clearCurrentEditingPublication(): void {
    this._currentEditingPublication$.next(undefined);
  }

  switchToPublicationEditState(): void {
    this._currentViewState$.next(UserProfileViewState.EDIT);
  }

  switchToPublicationViewState(): void {
    this._currentViewState$.next(UserProfileViewState.VIEW);
  }
}
